import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Icon,
  Card,
  CardContent,
  Divider,
  Button,
  alpha,
} from "@mui/material";
import { VendorQuotation } from "../../types/rfq.types";
import { hasPermission } from "../../../auth/utils/permission.utils";
import { useAuth } from "../../../auth/hooks/useAuth";
interface VendorQuotationComparisonProps {
  quotations: VendorQuotation[];
  onAcceptQuotation?: (quotationId: string) => void;
  onRejectQuotation?: (quotationId: string) => void;
  loading?: boolean;
}

export const VendorQuotationComparison: React.FC<
  VendorQuotationComparisonProps
> = ({ quotations, onAcceptQuotation, onRejectQuotation, loading = false }) => {
  const { user } = useAuth();
  const formatCurrency = (amount: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const isQuotationExpired = (validityTimestamp: number) => {
    return validityTimestamp < Date.now();
  };

  // Calculate comparison metrics
  const validQuotations = quotations.filter(
    (q) =>
      q.status === "submitted" && !isQuotationExpired(q.quote_validity_date)
  );

  const bestPrice =
    validQuotations.length > 0
      ? Math.min(...validQuotations.map((q) => q.quoted_price))
      : 0;

  const fastestDelivery =
    validQuotations.length > 0
      ? Math.min(...validQuotations.map((q) => q.estimated_delivery_days))
      : 0;

  const getComparisonScore = (quotation: VendorQuotation) => {
    if (
      quotation.status !== "submitted" ||
      isQuotationExpired(quotation.quote_validity_date)
    ) {
      return { price: 0, delivery: 0, overall: 0 };
    }

    const priceScore =
      bestPrice > 0 ? (bestPrice / quotation.quoted_price) * 50 : 0;
    const deliveryScore =
      fastestDelivery > 0
        ? (fastestDelivery / quotation.estimated_delivery_days) * 50
        : 0;
    const overall = priceScore + deliveryScore;

    return {
      price: Math.round(priceScore),
      delivery: Math.round(deliveryScore),
      overall: Math.round(overall),
    };
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "#059669"; // Green
    if (score >= 60) return "#F59E0B"; // Orange
    return "#DC2626"; // Red
  };

  const getStatusChip = (quotation: VendorQuotation) => {
    const isExpired = isQuotationExpired(quotation.quote_validity_date);

    if (isExpired) {
      return (
        <Chip
          label="EXPIRED"
          sx={{
            bgcolor: "#FEF2F2",
            color: "#DC2626",
            fontWeight: 600,
            fontSize: 12,
          }}
        />
      );
    }

    const config = {
      pending: { label: "Pending", color: "#64748B", bgColor: "#F1F5F9" },
      submitted: { label: "Submitted", color: "#3B82F6", bgColor: "#EFF6FF" },
      accepted: { label: "Accepted", color: "#059669", bgColor: "#ECFDF5" },
      rejected: { label: "Rejected", color: "#DC2626", bgColor: "#FEF2F2" },
    };

    const statusConfig = config[quotation.status];
    return (
      <Chip
        label={statusConfig.label}
        sx={{
          bgcolor: statusConfig.bgColor,
          color: statusConfig.color,
          fontWeight: 600,
          fontSize: 12,
        }}
      />
    );
  };

  const sortedQuotations = [...quotations].sort((a, b) => {
    const scoreA = getComparisonScore(a).overall;
    const scoreB = getComparisonScore(b).overall;
    return scoreB - scoreA; // Sort by best score first
  });

  if (quotations.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", bgcolor: "grey.50" }}>
        <Icon sx={{ fontSize: 48, color: "grey.400", mb: 2 }}>
          compare_arrows
        </Icon>
        <Typography variant="h6" gutterBottom>
          No Quotations to Compare
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Add vendor quotations to see comparison analysis
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: "#ECFDF5", border: "1px solid #059669" }}>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h6" color="#059669" fontWeight={600}>
                {formatCurrency(bestPrice)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Best Price
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: "#EFF6FF", border: "1px solid #3B82F6" }}>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h6" color="#3B82F6" fontWeight={600}>
                {fastestDelivery} days
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Fastest Delivery
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: "#F3E8FF", border: "1px solid #7C3AED" }}>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h6" color="#7C3AED" fontWeight={600}>
                {validQuotations.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Valid Quotations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quotations Comparison */}
      <Grid container spacing={2}>
        {sortedQuotations.map((quotation, index) => {
          const score = getComparisonScore(quotation);
          const isBestPrice =
            quotation.quoted_price === bestPrice && validQuotations.length > 0;
          const isFastestDelivery =
            quotation.estimated_delivery_days === fastestDelivery &&
            validQuotations.length > 0;
          const isExpired = isQuotationExpired(quotation.quote_validity_date);

          return (
            <Grid item xs={12} md={6} lg={4} key={quotation._id}>
              <Card
                sx={{
                  height: "100%",
                  position: "relative",
                  border:
                    index === 0 && score.overall > 0
                      ? "2px solid #059669"
                      : undefined,
                  opacity: isExpired ? 0.7 : 1,
                }}
              >
                {/* Best Overall Badge */}
                {index === 0 && score.overall > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -8,
                      left: 16,
                      bgcolor: "#059669",
                      color: "white",
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: 12,
                      fontWeight: 600,
                      zIndex: 1,
                    }}
                  >
                    BEST OVERALL
                  </Box>
                )}

                <CardContent
                  sx={{ pt: index === 0 && score.overall > 0 ? 3 : 2 }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      {quotation.supplier_name}
                    </Typography>
                    {getStatusChip(quotation)}
                  </Box>

                  {/* Price with Best Price Badge */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="primary.main"
                      >
                        {formatCurrency(
                          quotation.quoted_price,
                          quotation.currency
                        )}
                      </Typography>
                      {isBestPrice && (
                        <Chip
                          label="BEST PRICE"
                          size="small"
                          sx={{
                            bgcolor: "#ECFDF5",
                            color: "#059669",
                            fontWeight: 600,
                            fontSize: 10,
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Delivery with Fastest Badge */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1" fontWeight={600}>
                        {quotation.estimated_delivery_days} days delivery
                      </Typography>
                      {isFastestDelivery && (
                        <Chip
                          label="FASTEST"
                          size="small"
                          sx={{
                            bgcolor: "#EFF6FF",
                            color: "#3B82F6",
                            fontWeight: 600,
                            fontSize: 10,
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Validity */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Valid until: {formatDate(quotation.quote_validity_date)}
                    </Typography>
                  </Box>

                  {/* Terms */}
                  {(quotation.payment_terms || quotation.delivery_terms) && (
                    <Box sx={{ mb: 2 }}>
                      <Divider sx={{ mb: 1 }} />
                      {quotation.payment_terms && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Payment:</strong> {quotation.payment_terms}
                        </Typography>
                      )}
                      {quotation.delivery_terms && (
                        <Typography variant="body2">
                          <strong>Delivery:</strong> {quotation.delivery_terms}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Score */}
                  {score.overall > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Divider sx={{ mb: 1 }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          Overall Score:
                        </Typography>
                        <Chip
                          label={`${score.overall}/100`}
                          sx={{
                            bgcolor: alpha(getScoreColor(score.overall), 0.1),
                            color: getScoreColor(score.overall),
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Actions */}
                  {quotation.status === "submitted" && !isExpired && (
                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Icon>check_circle</Icon>}
                        onClick={() => onAcceptQuotation?.(quotation._id)}
                        disabled={
                          !hasPermission(
                            "inventory.rfq_quotation.accept",
                            user?.newPermissions,
                            user?.assignment_details?.id,
                            user?.is_store_owner
                          )
                        }
                        sx={{ flex: 1 }}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<Icon>cancel</Icon>}
                        onClick={() => onRejectQuotation?.(quotation._id)}
                        disabled={
                          !hasPermission(
                            "inventory.rfq_quotation.reject",
                            user?.newPermissions,
                            user?.assignment_details?.id,
                            user?.is_store_owner
                          )
                        }
                      >
                        Reject
                      </Button>
                    </Box>
                  )}

                  {/* Notes */}
                  {quotation.notes && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #E5E7EB" }}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Notes:</strong> {quotation.notes}
                      </Typography>
                    </Box>
                  )}

                  {/* Rejection Reason */}
                  {quotation.rejection_reason && (
                    <Box
                      sx={{ mt: 2, p: 1, bgcolor: "error.50", borderRadius: 1 }}
                    >
                      <Typography variant="body2" color="error.main">
                        <strong>Rejected:</strong> {quotation.rejection_reason}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
